import React, { ReactNode, useRef, useState } from 'react';
import { WarningCircleIcon } from '@fronton/icons-react';
import { Hint, HintCloseButton, Typography, HintProps as FrontonHintProps } from 'fronton-react';

import { HintContentWrapper, HintButton, hintButtonWrapper } from './styled';
import { useCloseHintOutside } from './use-close-hint-outside';
import { IconColors, IconSizes } from '@fronton/icons-react/types';

type HintProps = {
	header?: string;
	paragraph?: string;
	description?: string[];
	contentWidth?: number;
	hintClassName?: string;
	customAnchor?: ReactNode;
	hideIcon?: boolean;
	iconColor?: IconColors;
	clickCallback?: () => void;
	size?: IconSizes;
	placement?: FrontonHintProps['placement'];
};

export const FrontonHint = ({
	header,
	paragraph,
	description,
	contentWidth = 0,
	hintClassName,
	hideIcon,
	customAnchor,
	iconColor,
	clickCallback,
	size,
	placement = 'top',
	...rest
}: HintProps) => {
	const btnRef = useRef(null);
	const hintRef = useRef(null);

	const [isOpen, setIsOpen] = useState(false);

	const onClose = () => setIsOpen(false);

	const onToggle = (event: MouseEvent) => {
		event.preventDefault();
		setIsOpen(prev => !prev);
		clickCallback?.();
	};

	useCloseHintOutside(btnRef, hintRef, onClose);

	return (
		<Hint
			className={hintButtonWrapper}
			open={isOpen}
			anchor={
				<HintButton onClick={onToggle} {...rest}>
					{customAnchor}
					{!hideIcon && (
						<WarningCircleIcon
							strokeWidth={2}
							ref={btnRef}
							type="outline"
							size={size || 'm'}
							color={iconColor || 'text-primary'}
						/>
					)}
				</HintButton>
			}
			placement={placement}
			closeButton={<HintCloseButton onClick={onClose} />}
		>
			<HintContentWrapper ref={hintRef} contentWidth={contentWidth} className={hintClassName}>
				{header && (
					<Typography variant="s" size="body_accent" key={header}>
						{header}
					</Typography>
				)}
				{paragraph && (
					<Typography variant="s" size="body_short" key={paragraph}>
						{paragraph}
					</Typography>
				)}
				{description?.map(item => (
					<Typography variant="s" size="body_short" key={item}>
						{item}
					</Typography>
				))}
			</HintContentWrapper>
		</Hint>
	);
};

FrontonHint.defaultProps = {
	contentWidth: 0,
	header: null,
	paragraph: null,
	description: null,
	hintClassName: '',
	hideIcon: false,
};
